import isFunction from 'lodash/isFunction';
import compact from 'lodash/compact';
import {
  MediaOriginatorType,
} from 'pages/Dashboard/utils/constants';
import {
  getApiToDoGetToDoById,
  postApiToDoRequestToDoList,
} from 'endpoints';
import {
  ToDo,
} from 'types/toDo';
import {
  TodoDataParams,
} from 'pages/Dashboard/pages/Todos/types';
import {
  normalizeToDos,
  normalizeToDo,
} from 'pages/Dashboard/pages/Todos/normalize';
import {
  ToDoPaginatedCollectionDTO,
  ToDoRequestDTO,
} from 'dtos';
import {
  serializeDateRange,
} from 'pages/Dashboard/transport/dateRange';
import {
  toDosEventChannel,
} from 'pages/Dashboard/services/eventChannels';

export const buildToDoListRequest = ({
  pagination,
  dateRange,
  filterQuery,
  pillFilters,
  searchQuery,
  patientId,
  sortRequest,
}: Partial<TodoDataParams>): ToDoRequestDTO => ({
    includeReferencedObjects: true,
    patientSearchText: searchQuery,
    dateRange: serializeDateRange(dateRange),
    filterQuery,
    pillFilters,
    patientId,
    sortRequest,
    ...(pagination ? {
      pageRequest: {
        page: pagination?.pageIndex ?? 0,
        pageSize: pagination?.pageSize ?? 25,
      },
    } : {}),
  });

export const getToDosResponse = async (
  query: Partial<TodoDataParams>,
): Promise<ToDoPaginatedCollectionDTO> => postApiToDoRequestToDoList(buildToDoListRequest(query));

export const getDetailedToDo = async (toDoId: number): Promise<ToDo> => {
  const data = await getApiToDoGetToDoById({
    toDoId,
    includeReferencedObjects: true,
  });
  const { users, patients, ...response } = data ?? {};
  return normalizeToDo(
    response as ToDo,
    users ?? {},
    patients ?? {},
  );
};

export const getToDosData = async ({
  pagination,
  dateRange,
  setTotalSize,
  requestMedia,
  filterQuery,
  pillFilters,
  searchQuery,
  patientId,
  sortRequest,
}: TodoDataParams): Promise<ToDo[]> => {
  const response = await getToDosResponse({
    pagination,
    dateRange,
    filterQuery,
    pillFilters,
    searchQuery,
    patientId,
    sortRequest,
  });

  toDosEventChannel.emit('onToDoSummaryChanged', response.summary ?? { counts: {} });

  const meta = response.toDos?.pageProperties;
  setTotalSize(meta?.totalSize ?? 0);

  const normalize = normalizeToDos(response);

  if (isFunction(requestMedia)) {
    const patientIds = compact(normalize.map((todo) => todo.patientId));
    requestMedia(patientIds, MediaOriginatorType.Patient);

    const userIds = compact(
      normalize.flatMap((todo) => [
        ...(todo.assignedUsers?.map(({ userId }) => userId) ?? []),
        ...(todo.collaboratorUsers?.map(({ userId }) => userId) ?? []),
        todo.creator?.userId,
        todo.assignee?.userId,
      ]),
    );
    requestMedia(userIds, MediaOriginatorType.User);
  }

  return normalize;
};
